/* START_MODULE_CONTRACT
 * PURPOSE: React-контекст общего списка проектов: загрузка, ошибка, обновление.
 * SCOPE: ProjectsProvider (fetch/refetch проектов), useProjects.
 * DEPENDS: M-FE-API
 * LINKS: M-FE-PROJECTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * ProjectsProvider - провайдер состояния списка проектов.
 * useProjects - хук доступа к контексту.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-02 - GRACE coverage: контракты, END_METHOD, блоки и логирование fetch.
 * END_CHANGE_SUMMARY
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getProjects } from '../api/projectApi';
import Project from '../models/Project';

const ProjectsContext = createContext(null);

/* START_METHOD_ProjectsProvider */
/* START_CONTRACT: ProjectsProvider
 * PURPOSE: Провайдер общего состояния проектов с загрузкой при монтировании.
 * INPUTS: { children: ReactNode }
 * OUTPUTS: { JSX.Element - ProjectsContext.Provider }
 * SIDE_EFFECTS: HTTP getProjects; хранит projects/loading/error.
 * LINKS: M-FE-PROJECTS, M-PROJECT
 * END_CONTRACT: ProjectsProvider
 */
export function ProjectsProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* START_METHOD_fetchProjects */
  /* START_CONTRACT: fetchProjects
   * PURPOSE: Загрузить проекты и обновить состояние; корректно обработать отмену и ошибку.
   * INPUTS: { signal: AbortSignal }
   * OUTPUTS: { Promise<void> }
   * SIDE_EFFECTS: setProjects/setError/setLoading; лог веток LOAD_OK/LOAD_FAIL.
   * LINKS: M-PROJECT
   * END_CONTRACT: fetchProjects
   */
  const fetchProjects = useCallback(async (signal) => {
    try {
      const { data } = await getProjects(signal);
      // START_BLOCK_LOAD_OK
      setProjects(data.map(Project.fromResponse));
      setError('');
      // END_BLOCK_LOAD_OK
    } catch (err) {
      // START_BLOCK_LOAD_FAIL
      if (signal?.aborted) return;
      console.warn('[FeProjects][fetchProjects][BLOCK_LOAD_FAIL] projects load failed');
      setError(err.response?.data?.message || 'Ошибка загрузки проектов');
      // END_BLOCK_LOAD_FAIL
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);
  /* END_METHOD_fetchProjects */

  useEffect(() => {
    const controller = new AbortController();
    fetchProjects(controller.signal);
    return () => controller.abort();
  }, [fetchProjects]);

  const refetch = useCallback(() => fetchProjects(), [fetchProjects]);

  const value = useMemo(
    () => ({ projects, loading, error, refetch }),
    [projects, loading, error, refetch],
  );

  return <ProjectsContext.Provider value={value}>{children}</ProjectsContext.Provider>;
}
/* END_METHOD_ProjectsProvider */

/* START_METHOD_useProjects */
/* START_CONTRACT: useProjects
 * PURPOSE: Хук доступа к контексту проектов с проверкой провайдера.
 * INPUTS: нет
 * OUTPUTS: { { projects, loading, error, refetch } }
 * SIDE_EFFECTS: Бросает Error вне ProjectsProvider.
 * LINKS: M-FE-PROJECTS
 * END_CONTRACT: useProjects
 */
export function useProjects() {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjects must be used within ProjectsProvider');
  }
  return context;
}
/* END_METHOD_useProjects */
